import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Col, Form, Input, Row } from "antd";
import { useEffect } from "react";
import { EUserRole, ServiceTemplateField, TRANSLATABLE_METADATA_TEMPLATE_ATTRIBUTES_LIST, TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { useTranslation } from "../../../../../others/i18n.js";
import { spacing } from "../../../../../styles/spacing.js";
import { getFieldNameTranslated } from "../../../../../util/template/translation.js";
import { FormFieldVisibilityWrapper } from "../formFieldVisibilityWrapper.js";
const MIN_REQUIRED_CDN_VERSION = "4.8.0";
Object.freeze(MIN_REQUIRED_CDN_VERSION);
/**
 * Form field for the minimum required RCB version.
 *
 * Exception: When CDN is enabled, the minimum required version must be 4.8.0 or higher.
 *
 * @returns The form field component for minimum required RCB version.
 */ function FormFieldTemplateMinRequiredRcbVersion() {
    const { t } = useTranslation();
    const form = Form.useFormInstance();
    const isCdn = Form.useWatch(ServiceTemplateField.IsCdn, form);
    const currentVersion = Form.useWatch(TemplateMetaDataField.MinRequiredRcbVersion, form);
    useEffect(()=>{
        form.validateFields([
            TemplateMetaDataField.MinRequiredRcbVersion
        ]);
    }, [
        isCdn,
        form
    ]);
    const setMinimumVersion = ()=>{
        form.setFieldsValue({
            [TemplateMetaDataField.MinRequiredRcbVersion]: MIN_REQUIRED_CDN_VERSION
        });
        form.validateFields([
            TemplateMetaDataField.MinRequiredRcbVersion
        ]);
    };
    const isVersionAtLeastMinimum = (version)=>{
        const [major, minor] = version.split(".").map(Number);
        const [minMajor, minMinor] = MIN_REQUIRED_CDN_VERSION.split(".").map(Number);
        return major > minMajor || major === minMajor && minor >= minMinor;
    };
    const showSetVersionButton = isCdn && (!currentVersion || !isVersionAtLeastMinimum(currentVersion));
    return /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
        fieldNames: TemplateMetaDataField.MinRequiredRcbVersion,
        permissionCheck: {
            whenRole: EUserRole.Translator,
            roleWhiteList: TRANSLATABLE_METADATA_TEMPLATE_ATTRIBUTES_LIST
        },
        children: /*#__PURE__*/ _jsx(Form.Item, {
            label: getFieldNameTranslated(TemplateMetaDataField.MinRequiredRcbVersion, t),
            extra: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Row, {
                        children: /*#__PURE__*/ _jsx(Col, {
                            children: t("Set a version, if the template depends on some integration code in the WordPress plugin.")
                        })
                    }),
                    /*#__PURE__*/ _jsx(Row, {
                        hidden: !showSetVersionButton,
                        style: {
                            marginBottom: spacing.layout.content.marginMedium
                        },
                        children: /*#__PURE__*/ _jsx(Col, {
                            children: /*#__PURE__*/ _jsx(Button, {
                                type: "default",
                                onClick: setMinimumVersion,
                                children: t("Set minimum required version for CDN")
                            })
                        })
                    })
                ]
            }),
            name: TemplateMetaDataField.MinRequiredRcbVersion,
            rules: [
                {
                    required: true,
                    message: t("Please enter a version number.")
                },
                {
                    pattern: /^(?<major>\d{1,4})\.(?<minor>\d{1,4})\.(?<patch>\d{1,4})(?:-(?<prerelease>\d{1,7}))?$/s,
                    message: t("Minimum required RCB version must be formatted properly. Samples: 3.2.1 or 3.2.1-14 or 1234.1234.1234-1234567")
                },
                {
                    validator: (_, value)=>{
                        if (isCdn && value && !isVersionAtLeastMinimum(value)) {
                            return Promise.reject(new Error(t(`CDN service templates, are only supported since version ${MIN_REQUIRED_CDN_VERSION}`)));
                        }
                        return Promise.resolve();
                    }
                }
            ],
            children: /*#__PURE__*/ _jsx(Input, {
                placeholder: "3.0.5-1990",
                maxLength: 22
            })
        })
    });
}
export { FormFieldTemplateMinRequiredRcbVersion, MIN_REQUIRED_CDN_VERSION };
